import {
  type EpocusEncounter,
  type EpocusEncounterQueryConstructor,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { useUser } from "$global-providers/user-provider/provider";
import { useMemo } from "react";
import { useRxData } from "rxdb-hooks";

export type ToPerformEntry = {
  encounter: EpocusEncounter;
  unassigned: boolean;
};

const orderedQuery: EpocusEncounterQueryConstructor = (collection) =>
  collection?.find({
    selector: { status: "ordered" },
    sort: [{ date: "desc" }],
    limit: 100,
  });

const disabledQuery: EpocusEncounterQueryConstructor = () => undefined;

const NO_ENTRIES: ToPerformEntry[] = [];

const urgencyRank = (encounter: EpocusEncounter): number =>
  encounter.requestPriority === "urgent" ? 0 : 1;

const requestedTime = (encounter: EpocusEncounter): number =>
  new Date(encounter.requestedAt ?? encounter.date).getTime();

export const useToPerformQueue = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const user = useUser();
  const { result: encounters, isFetching } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    enabled ? orderedQuery : disabledQuery,
  );

  const entries = useMemo<ToPerformEntry[]>(() => {
    if (!enabled) return NO_ENTRIES;

    return encounters
      .filter(
        (encounter) =>
          encounter.performerId === null || encounter.performerId === user.id,
      )
      .map((encounter) => ({
        encounter,
        unassigned: encounter.performerId === null,
      }))
      .sort(
        (a, b) =>
          urgencyRank(a.encounter) - urgencyRank(b.encounter) ||
          requestedTime(a.encounter) - requestedTime(b.encounter),
      );
  }, [enabled, encounters, user.id]);

  if (!enabled) return { entries: NO_ENTRIES, loading: false };

  return { entries, loading: isFetching };
};
